import React, { useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    Input,
    Modal,
    Space,
    message,
    Spin,
    Tabs,
    Popconfirm,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";

import {
    fetchPostLinks,
    createPostLink,
    updatePostLink,
    deletePostLink,
} from "../api/postLinksApi";

const { TextArea } = Input;

export default function LinkPublisherPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState([]);
    const [singleTitle, setSingleTitle] = useState("");
    const [singleLink, setSingleLink] = useState("");
    const [bulkLinks, setBulkLinks] = useState("");

    useEffect(() => {
        load();
    }, []);

    async function load() {
        setLoading(true);
        try {
            const data = await fetchPostLinks();
            setItems(data.items || []);
        } catch (e) {
            message.error("Erro ao carregar links");
        } finally {
            setLoading(false);
        }
    }

    function toggleSelected(id) {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }

    async function handleTogglePublished(item, published) {
        try {
            const { item: updated } = await updatePostLink(item.id, { published });
            setItems((prev) => prev.map((p) => (p.id === item.id ? updated : p)));
        } catch (e) {
            message.error("Erro ao atualizar");
        }
    }

    async function handleDelete(item) {
        try {
            await deletePostLink(item.id);
            setItems((prev) => prev.filter((p) => p.id !== item.id));
            setSelected((prev) => prev.filter((id) => id !== item.id));
            message.success("Link removido");
        } catch (e) {
            message.error("Erro ao remover");
        }
    }

    async function handleDeleteSelected() {
        try {
            await Promise.all(selected.map((id) => deletePostLink(id)));
            setItems((prev) => prev.filter((p) => !selected.includes(p.id)));
            setSelected([]);
            message.success("Links removidos");
        } catch (e) {
            message.error("Erro ao remover");
        }
    }

    async function handleCreateSingle() {
        if (!singleLink.trim()) {
            message.warning("Cole um link primeiro");
            return;
        }

        try {
            const payload = {
                title: singleTitle.trim() || "Link cadastrado",
                url: singleLink.trim(),
                published: false,
            };

            const { item } = await createPostLink(payload);

            setItems((prev) => [item, ...prev]);
            setSingleTitle("");
            setSingleLink("");
            setOpen(false);

            message.success("Link cadastrado");
        } catch (e) {
            console.error("Erro ao cadastrar link:", e);
            message.error(e.message || "Erro ao cadastrar link");
        }
    }

    async function handleCreateBulk() {
        const links = bulkLinks
            .split("\n")
            .map((link) => link.trim())
            .filter(Boolean);

        if (!links.length) {
            message.warning("Cole pelo menos um link");
            return;
        }

        try {
            const createdItems = [];

            for (const link of links) {
                const { item } = await createPostLink({
                    title: "Link cadastrado",
                    url: link,
                    published: false,
                });

                createdItems.push(item);
            }

            setItems((prev) => [...createdItems, ...prev]);
            setBulkLinks("");
            setOpen(false);

            message.success(`${createdItems.length} links cadastrados`);
        } catch (e) {
            message.error("Erro ao cadastrar links");
        }
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                display: "grid",
                gridTemplateColumns: "1fr 420px",
                background: "linear-gradient(180deg, #0b0b0c, #060607)",
                color: "white",
            }}
        >
            <div
                style={{
                    padding: 24,
                    borderRight: "1px solid rgba(255,255,255,0.08)",
                }}
            >
                <h1 style={{ marginBottom: 6 }}>Links cadastrados</h1>

                <p style={{ color: "#aaa", marginBottom: 20 }}>
                    Marque como publicado ou selecione vários para excluir.
                </p>

                {selected.length > 0 && (
                    <div
                        style={{
                            marginBottom: 20,
                            padding: 14,
                            borderRadius: 10,
                            background: "rgba(255,255,255,0.08)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            border: "1px solid rgba(255,255,255,0.12)",
                        }}
                    >
                        <strong>{selected.length} selecionado(s)</strong>

                        <Popconfirm
                            title="Excluir links selecionados?"
                            okText="Excluir"
                            cancelText="Cancelar"
                            onConfirm={handleDeleteSelected}
                        >
                            <Button danger icon={<DeleteOutlined />}>
                                Excluir selecionados
                            </Button>
                        </Popconfirm>
                    </div>
                )}

                {loading ? (
                    <div style={{ padding: 30 }}>
                        <Spin />
                    </div>
                ) : items.length === 0 ? (
                    <div
                        style={{
                            padding: 24,
                            border: "1px dashed rgba(255,255,255,0.2)",
                            borderRadius: 12,
                            color: "#aaa",
                        }}
                    >
                        Nenhum link cadastrado ainda.
                    </div>
                ) : (
                    <Space direction="vertical" style={{ width: "100%" }} size={12}>
                        {items.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    display: "flex",
                                    gap: 12,
                                    alignItems: "flex-start",
                                    padding: 14,
                                    borderRadius: 12,
                                    background: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                }}
                            >
                                <Checkbox
                                    checked={selected.includes(item.id)}
                                    onChange={() => toggleSelected(item.id)}
                                />

                                <Checkbox
                                    checked={Boolean(item.published)}
                                    onChange={(e) =>
                                        handleTogglePublished(item, e.target.checked)
                                    }
                                >
                                    <span style={{ color: "white" }}>Publicado</span>
                                </Checkbox>

                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700 }}>
                                        {item.title || "Link cadastrado"}
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 6,
                                            fontSize: 13,
                                            color: "#aaa",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {item.url}
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 8,
                                            fontSize: 12,
                                            color: item.published ? "#4ade80" : "#facc15",
                                        }}
                                    >
                                        {item.published ? "Publicado" : "Pendente"}
                                    </div>
                                </div>

                                <Popconfirm
                                    title="Excluir este link?"
                                    okText="Excluir"
                                    cancelText="Cancelar"
                                    onConfirm={() => handleDelete(item)}
                                >
                                    <Button danger type="text" icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </div>
                        ))}
                    </Space>
                )}
            </div>

            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 30,
                }}
            >
                <Button
                    type="primary"
                    size="large"
                    style={{
                        height: 60,
                        paddingInline: 40,
                        fontSize: 18,
                        fontWeight: 600,
                    }}
                    onClick={() => setOpen(true)}
                >
                    Cadastrar Link
                </Button>
            </div>

            <Modal
                open={open}
                title="Cadastrar Link"
                footer={null}
                destroyOnClose
                onCancel={() => setOpen(false)}
            >
                <Tabs
                    defaultActiveKey="1"
                    items={[
                        {
                            key: "1",
                            label: "Um link",
                            children: (
                                <Space direction="vertical" style={{ width: "100%" }} size={12}>
                                    <Input
                                        placeholder="Título"
                                        value={singleTitle}
                                        onChange={(e) => setSingleTitle(e.target.value)}
                                    />

                                    <Input
                                        placeholder="Cole o link"
                                        value={singleLink}
                                        onChange={(e) => setSingleLink(e.target.value)}
                                    />

                                    <Button type="primary" block onClick={handleCreateSingle}>
                                        Salvar
                                    </Button>
                                </Space>
                            ),
                        },
                        {
                            key: "2",
                            label: "Em massa",
                            children: (
                                <Space direction="vertical" style={{ width: "100%" }} size={12}>
                                    <TextArea
                                        rows={8}
                                        placeholder={`Cole um link por linha

https://link1.com
https://link2.com
https://link3.com`}
                                        value={bulkLinks}
                                        onChange={(e) => setBulkLinks(e.target.value)}
                                    />

                                    <Button type="primary" block onClick={handleCreateBulk}>
                                        Salvar Todos
                                    </Button>
                                </Space>
                            ),
                        },
                    ]}
                />
            </Modal>
        </div>
    );
}